import { Box, Flex, Heading } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import Container from './Container';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import Link from 'next/link';
import { jsonLdScriptProps } from 'react-schemaorg';
import { BreadcrumbList, WithContext } from 'schema-dts';
import Head from 'next/head';
import getAppConfig from '../../utils/getAppConfig';

interface PageHeaderProps {
  title: string;
  preTitle?: JSX.Element;
  TitleAdditionComponent?: JSX.Element;
  subTitle?: string | JSX.Element;
  ActionComponent?: JSX.Element;
  breadcrumb?: Array<{ href: string; label: string; isCurrentPage?: boolean }>;
}

export default function PageHeader({
  preTitle,
  title,
  subTitle,
  ActionComponent,
  TitleAdditionComponent,
  breadcrumb,
}: PageHeaderProps) {
  const appConfig = getAppConfig();

  const filteredBreadcrumb = useMemo(() => {
    if (breadcrumb) {
      return breadcrumb.filter((breadcrumbItem) =>
        Boolean(breadcrumbItem.label)
      );
    }

    return [];
  }, [breadcrumb]);

  const jsonLD = useMemo<WithContext<BreadcrumbList> | null>(() => {
    if (filteredBreadcrumb) {
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: filteredBreadcrumb.map((item, i) => {
          return {
            '@type': 'ListItem',
            position: i + 1,
            name: item.label,
            item: `${appConfig.config.fullUrl}${item.href}`,
          };
        }),
      };
    }

    return null;
  }, [filteredBreadcrumb]);

  return (
    <Box paddingY="8" borderBottom="1px" borderColor="gray.200" pos="relative">
      <Container>
        {filteredBreadcrumb && (
          <Box marginBottom={4}>
            {jsonLD && (
              <Head>
                <script {...jsonLdScriptProps<BreadcrumbList>(jsonLD)} />
              </Head>
            )}
            <Breadcrumb fontSize="sm">
              {filteredBreadcrumb.map((item) => {
                return (
                  <BreadcrumbItem key={item.href}>
                    <BreadcrumbLink
                      as={Link}
                      href={item.href}
                      isCurrentPage={!!item.isCurrentPage}
                    >
                      {item.label}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                );
              })}
            </Breadcrumb>
          </Box>
        )}
        <Flex
          alignItems={{
            base: 'flex-start',
            md: 'center',
          }}
          flexDirection={{
            base: 'column',
            md: 'row',
          }}
          justifyContent="space-between"
        >
          <Flex flexDirection="column">
            {preTitle && <Box>{preTitle}</Box>}

            <Flex
              flexDirection={{
                base: 'column',
                md: 'row',
              }}
              alignItems={{
                base: 'flex-start',
                md: 'center',
              }}
            >
              <Heading as="h1" fontWeight="bold">
                {title}
              </Heading>

              {TitleAdditionComponent}
            </Flex>

            {subTitle && (
              <Box
                marginY={{
                  base: 2,
                  md: 0,
                }}
                fontSize="sm"
              >
                {subTitle}
              </Box>
            )}
          </Flex>

          <Flex>{ActionComponent}</Flex>
        </Flex>
      </Container>
    </Box>
  );
}
